// ═══════════════════════════════════════════════════════════════
// HotWash — Incident Detail (3-tab view)
// Overview | Observations | AI Analysis
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Title, Text, Paper, Group, Badge, Tabs, SimpleGrid, Stack, Button,
  TextInput, SegmentedControl, ActionIcon, Modal, Textarea, Select, Checkbox,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  IconEye, IconChartBar, IconSparkles, IconPlus, IconSearch,
  IconLayoutGrid, IconList, IconTrash, IconChevronDown, IconChevronUp,
} from '@tabler/icons-react';
import { useHotWashStore } from '../../store/hotwashStore';
import { useAuthStore } from '../../store/authStore';
import { TIER_META, TIER_KEYS, CATEGORIES, PHASES } from '../../types';
import type { Observation, ObservationTier, ObservationCategory, ObservationPhase } from '../../types';
import { updateRecord, TABLES, FIELDS } from '../../lib/api-switch';
import AISummaryPanel from '../../components/hotwash/AISummaryPanel';
import AIAnalysis from '../../components/hotwash/AIAnalysis';
import ObservationModal from '../../components/hotwash/ObservationModal';
import ExportMenu from '../../components/hotwash/ExportTools';
import Breadcrumbs from '../../components/ui/Breadcrumbs';

export default function IncidentDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { observations, loadObservations, submitObservation, deleteObservation } = useHotWashStore();
  const { incidents, token } = useAuthStore();

  const incident = incidents.find(i => String(i.id) === id);
  const incObs = observations.filter(o => String(o.incidentId) === id);

  // Filters
  const [tierFilter, setTierFilter] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState('cards');
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});

  // Observation modal
  const [selectedObs, setSelectedObs] = useState<Observation | null>(null);
  const [obsModalOpened, { open: openObsModal, close: closeObsModal }] = useDisclosure(false);

  // Quick submit modal
  const [quickOpened, { open: openQuick, close: closeQuick }] = useDisclosure(false);
  const [qTier, setQTier] = useState<ObservationTier>('observation');
  const [qTitle, setQTitle] = useState('');
  const [qDesc, setQDesc] = useState('');
  const [qCategory, setQCategory] = useState<ObservationCategory>('communication');
  const [qPhase, setQPhase] = useState<ObservationPhase>('response');
  const [qRemedy, setQRemedy] = useState('');
  const [qAnon, setQAnon] = useState(false);
  const [qSubmitting, setQSubmitting] = useState(false);

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState<Observation | null>(null);
  const [deleteOpened, { open: openDelete, close: closeDelete }] = useDisclosure(false);

  useEffect(() => { loadObservations(); }, [loadObservations]);

  if (!incident) {
    return (
      <Paper p="xl" radius="sm" withBorder>
        <Text ta="center" c="dimmed">Incident not found.</Text>
        <Button variant="subtle" onClick={() => navigate('/hotwash')} mt="sm">Back to Dashboard</Button>
      </Paper>
    );
  }

  // Filter observations
  let filtered = incObs;
  if (tierFilter !== 'all') filtered = filtered.filter(o => o.tier === tierFilter);
  if (search) {
    const q = search.toLowerCase();
    filtered = filtered.filter(o => (o.title || '').toLowerCase().includes(q) || (o.description || '').toLowerCase().includes(q));
  }
  filtered = [...filtered].sort((a, b) => (b.datecreated ?? '').localeCompare(a.datecreated ?? ''));

  const tierCounts = TIER_KEYS.map(t => ({ tier: t, count: incObs.filter(o => o.tier === t).length }));

  const handleStatusChange = async (status: string) => {
    try {
      await updateRecord(TABLES.incidents, Number(id), [[FIELDS.incidents.status, status]], token ?? undefined);
      notifications.show({ title: 'Updated', message: `Status changed to ${status}.`, color: 'green' });
      // Reload to reflect change
      window.location.reload();
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to update status.', color: 'red' });
    }
  };

  const handleQuickSubmit = async () => {
    if (!qTitle.trim()) {
      notifications.show({ title: 'Required', message: 'Enter a title.', color: 'red' });
      return;
    }
    setQSubmitting(true);
    try {
      await submitObservation({
        incidentId: id!,
        tier: qTier,
        title: qTitle.trim(),
        description: qDesc.trim(),
        category: qCategory,
        phase: qPhase,
        suggestedRemedy: qRemedy.trim(),
        isAnonymous: qAnon,
      });
      closeQuick();
      setQTitle(''); setQDesc(''); setQRemedy(''); setQAnon(false);
      notifications.show({ title: 'Submitted', message: 'Observation recorded.', color: 'green' });
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to submit.', color: 'red' });
    } finally {
      setQSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    await deleteObservation(deleteTarget.id);
    closeDelete();
    setDeleteTarget(null);
  };

  const statusColor = (s: string) => s === 'active' ? 'green' : s === 'review' ? 'yellow' : 'gray';

  return (
    <div>
      <Breadcrumbs items={[
        { label: 'Hub', to: '/' },
        { label: 'HotWash', to: '/hotwash' },
        { label: 'Observations', to: '/hotwash/observations' },
        { label: incident?.name || 'Incident' },
      ]} />
      {/* Header */}
      <Group justify="space-between" mb="md">
        <div>
          <Group gap="xs" mb={4}>
            <Button variant="subtle" size="xs" onClick={() => navigate('/hotwash')} p={0} c="dimmed">
              HotWash
            </Button>
            <Text c="dimmed" size="xs">/</Text>
            <Text size="xs" fw={500}>{incident.name}</Text>
          </Group>
          <Title order={3}>{incident.name}</Title>
          <Group gap="xs" mt={4}>
            <Badge size="sm" color={statusColor(incident.status)} tt="capitalize">{incident.status}</Badge>
            <Badge size="sm" variant="outline" color="gray" tt="capitalize">{incident.incidentType.replace('_', ' ')}</Badge>
            {incident.location && <Text size="xs" c="dimmed">{incident.location}</Text>}
          </Group>
        </div>
        <Group gap="xs">
          <Button size="xs" leftSection={<IconPlus size={14} />} onClick={openQuick}>
            Observation
          </Button>
          <ExportMenu observations={incObs} incidents={incidents} incident={incident} />
        </Group>
      </Group>

      {/* Tabs */}
      <Tabs defaultValue="overview">
        <Tabs.List mb="md">
          <Tabs.Tab value="overview" leftSection={<IconEye size={14} />}>Overview</Tabs.Tab>
          <Tabs.Tab value="observations" leftSection={<IconChartBar size={14} />}>
            Observations ({incObs.length})
          </Tabs.Tab>
          <Tabs.Tab value="analysis" leftSection={<IconSparkles size={14} />}>AI Analysis</Tabs.Tab>
        </Tabs.List>

        {/* ── Overview Tab ── */}
        <Tabs.Panel value="overview">
          <Stack gap="md">
            {incObs.length > 0 && <AISummaryPanel observations={incObs} />}

            {/* Tier stat cards */}
            <SimpleGrid cols={{ base: 2, sm: 5 }}>
              <Paper p="sm" radius="sm" withBorder ta="center">
                <Text fz={24} fw={700}>{incObs.length}</Text>
                <Text size="xs" c="dimmed">Total</Text>
              </Paper>
              {tierCounts.map(t => {
                const meta = TIER_META[t.tier];
                return (
                  <Paper key={t.tier} p="sm" radius="sm" withBorder ta="center">
                    <Text fz={24} fw={700} style={{ color: meta.color }}>{t.count}</Text>
                    <Text size="xs" c="dimmed">{meta.label}</Text>
                  </Paper>
                );
              })}
            </SimpleGrid>

            {/* Recent observations */}
            <Paper p="md" radius="sm" withBorder>
              <Text fw={600} size="sm" mb="sm">Recent Observations</Text>
              {incObs.length === 0 ? (
                <Text size="sm" c="dimmed" ta="center" py="md">No observations yet.</Text>
              ) : (
                <Stack gap="xs">
                  {[...incObs].sort((a, b) => (b.datecreated ?? '').localeCompare(a.datecreated ?? '')).slice(0, 5).map(obs => {
                    const meta = TIER_META[obs.tier];
                    return (
                      <Paper
                        key={obs.id} p="xs" radius="sm" withBorder
                        style={{ borderLeft: `3px solid ${meta.color}`, cursor: 'pointer' }}
                        onClick={() => { setSelectedObs(obs); openObsModal(); }}
                      >
                        <Group gap="xs" mb={2}>
                          <Badge size="xs" style={{ background: meta.bg, color: meta.color }}>{meta.label}</Badge>
                          <Badge size="xs" variant="outline" color="gray" tt="capitalize">{obs.category}</Badge>
                        </Group>
                        <Text size="sm" fw={500}>{obs.title}</Text>
                        <Text size="xs" c="dimmed" lineClamp={1}>{obs.description}</Text>
                      </Paper>
                    );
                  })}
                </Stack>
              )}
            </Paper>

            {/* Incident info + Status controls */}
            <SimpleGrid cols={{ base: 1, md: 2 }}>
              <Paper p="md" radius="sm" withBorder>
                <Text fw={600} size="sm" mb="sm">Incident Details</Text>
                <SimpleGrid cols={2}>
                  <div><Text size="xs" c="dimmed">Type</Text><Text size="sm" tt="capitalize">{incident.incidentType.replace('_', ' ')}</Text></div>
                  <div><Text size="xs" c="dimmed">Location</Text><Text size="sm">{incident.location || '—'}</Text></div>
                  <div><Text size="xs" c="dimmed">Started</Text><Text size="sm">{incident.startDate || '—'}</Text></div>
                  <div><Text size="xs" c="dimmed">Created By</Text><Text size="sm">{incident.createdByName || '—'}</Text></div>
                </SimpleGrid>
                {incident.description && (
                  <div style={{ marginTop: 8 }}>
                    <Text size="xs" c="dimmed">Description</Text>
                    <Text size="sm">{incident.description}</Text>
                  </div>
                )}
              </Paper>

              <Paper p="md" radius="sm" withBorder>
                <Text fw={600} size="sm" mb="sm">Status</Text>
                <Group gap="xs">
                  {['active', 'review', 'closed'].map(s => (
                    <Button
                      key={s}
                      size="xs"
                      variant={incident.status === s ? 'filled' : 'outline'}
                      color={statusColor(s)}
                      tt="capitalize"
                      onClick={() => handleStatusChange(s)}
                    >
                      {s}
                    </Button>
                  ))}
                </Group>
              </Paper>
            </SimpleGrid>
          </Stack>
        </Tabs.Panel>

        {/* ── Observations Tab ── */}
        <Tabs.Panel value="observations">
          <Stack gap="md">
            {/* Filters */}
            <Group gap="xs" wrap="wrap">
              <SegmentedControl
                size="xs"
                value={tierFilter}
                onChange={setTierFilter}
                data={[
                  { value: 'all', label: `All (${incObs.length})` },
                  ...TIER_KEYS.map(t => ({
                    value: t,
                    label: `${TIER_META[t].label} (${incObs.filter(o => o.tier === t).length})`,
                  })),
                ]}
              />
              <TextInput
                placeholder="Search..."
                leftSection={<IconSearch size={14} />}
                value={search}
                onChange={e => setSearch(e.currentTarget.value)}
                size="xs"
                w={200}
              />
              <SegmentedControl
                size="xs"
                value={viewMode}
                onChange={setViewMode}
                data={[
                  { value: 'cards', label: <IconLayoutGrid size={14} /> },
                  { value: 'table', label: <IconList size={14} /> },
                ]}
              />
            </Group>

            <Text size="xs" c="dimmed">{filtered.length} observation{filtered.length !== 1 ? 's' : ''}</Text>

            {filtered.length === 0 ? (
              <Paper p="xl" radius="sm" withBorder>
                <Text ta="center" c="dimmed">No observations match.</Text>
              </Paper>
            ) : viewMode === 'cards' ? (
              <SimpleGrid cols={{ base: 1, md: 2 }}>
                {filtered.map(obs => {
                  const meta = TIER_META[obs.tier];
                  const isExp = expanded[obs.id];
                  return (
                    <Paper
                      key={obs.id} p="sm" radius="sm" withBorder
                      style={{ borderLeft: `3px solid ${meta.color}`, cursor: 'pointer' }}
                      onClick={() => { setSelectedObs(obs); openObsModal(); }}
                    >
                      <Group justify="space-between" wrap="nowrap" mb={4}>
                        <Group gap="xs">
                          <Badge size="xs" style={{ background: meta.bg, color: meta.color }}>{meta.label}</Badge>
                          <Badge size="xs" variant="outline" color="gray" tt="capitalize">{obs.category}</Badge>
                          <Badge size="xs" variant="outline" color="gray" tt="capitalize">{obs.phase}</Badge>
                        </Group>
                        <Group gap={4}>
                          <ActionIcon size="xs" variant="subtle" color="gray" onClick={e => { e.stopPropagation(); setExpanded(p => ({...p, [obs.id]: !p[obs.id]})); }}>
                            {isExp ? <IconChevronUp size={12} /> : <IconChevronDown size={12} />}
                          </ActionIcon>
                          <ActionIcon size="xs" variant="subtle" color="red" onClick={e => { e.stopPropagation(); setDeleteTarget(obs); openDelete(); }}>
                            <IconTrash size={12} />
                          </ActionIcon>
                        </Group>
                      </Group>
                      <Text size="sm" fw={500}>{obs.title}</Text>
                      <Text size="xs" c="dimmed" lineClamp={isExp ? undefined : 2}>{obs.description}</Text>
                      {isExp && obs.suggestedRemedy && (
                        <Paper p="xs" mt="xs" radius="sm" bg="var(--success-light)" style={{ border: '1px solid var(--success)' }}>
                          <Text size="xs" fw={600} mb={2}>Suggested Remedy</Text>
                          <Text size="xs">{obs.suggestedRemedy}</Text>
                        </Paper>
                      )}
                      <Group justify="space-between" mt="xs">
                        <Text size="xs" c="dimmed">{obs.submittedBy}</Text>
                        <Text size="xs" c="dimmed">{obs.datecreated ? new Date(obs.datecreated).toLocaleDateString() : ''}</Text>
                      </Group>
                    </Paper>
                  );
                })}
              </SimpleGrid>
            ) : (
              <Paper radius="sm" withBorder style={{ overflow: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: 'var(--bg-secondary)', borderBottom: 'var(--border)' }}>
                      <th style={{ padding: '8px 12px', textAlign: 'left' }}>Tier</th>
                      <th style={{ padding: '8px 12px', textAlign: 'left' }}>Title</th>
                      <th style={{ padding: '8px 12px', textAlign: 'left' }}>Category</th>
                      <th style={{ padding: '8px 12px', textAlign: 'left' }}>Phase</th>
                      <th style={{ padding: '8px 12px', textAlign: 'left' }}>By</th>
                      <th style={{ padding: '8px 12px' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map(obs => {
                      const meta = TIER_META[obs.tier];
                      return (
                        <tr
                          key={obs.id}
                          style={{ borderBottom: '1px solid var(--frost)', cursor: 'pointer' }}
                          onClick={() => { setSelectedObs(obs); openObsModal(); }}
                        >
                          <td style={{ padding: '8px 12px' }}>
                            <Badge size="xs" style={{ background: meta.bg, color: meta.color }}>{meta.label}</Badge>
                          </td>
                          <td style={{ padding: '8px 12px' }}>{obs.title}</td>
                          <td style={{ padding: '8px 12px', textTransform: 'capitalize' }}>{obs.category}</td>
                          <td style={{ padding: '8px 12px', textTransform: 'capitalize' }}>{obs.phase}</td>
                          <td style={{ padding: '8px 12px' }}>{obs.submittedBy}</td>
                          <td style={{ padding: '8px 12px' }}>
                            <ActionIcon size="xs" variant="subtle" color="red" onClick={e => { e.stopPropagation(); setDeleteTarget(obs); openDelete(); }}>
                              <IconTrash size={12} />
                            </ActionIcon>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </Paper>
            )}
          </Stack>
        </Tabs.Panel>

        {/* ── AI Analysis Tab ── */}
        <Tabs.Panel value="analysis">
          <AIAnalysis incident={incident} observations={incObs} />
        </Tabs.Panel>
      </Tabs>

      {/* Observation Detail Modal */}
      <ObservationModal
        observation={selectedObs}
        opened={obsModalOpened}
        onClose={closeObsModal}
        onDelete={(id) => { deleteObservation(id); }}
      />

      {/* Quick Submit Modal */}
      <Modal opened={quickOpened} onClose={closeQuick} title="Submit Observation" size="lg">
        <Stack gap="sm">
          <Group gap="xs" grow>
            {TIER_KEYS.map(t => {
              const meta = TIER_META[t];
              return (
                <Paper
                  key={t}
                  p="xs"
                  radius="sm"
                  withBorder
                  style={{
                    cursor: 'pointer',
                    borderColor: qTier === t ? meta.color : undefined,
                    background: qTier === t ? meta.bg : undefined,
                  }}
                  onClick={() => setQTier(t)}
                >
                  <Text size="xs" fw={600} style={{ color: meta.color }}>{meta.label}</Text>
                  <Text size="xs" c="dimmed">{meta.desc}</Text>
                </Paper>
              );
            })}
          </Group>
          <TextInput label="Title" required value={qTitle} onChange={e => setQTitle(e.currentTarget.value)} />
          <Textarea label="Description" value={qDesc} onChange={e => setQDesc(e.currentTarget.value)} minRows={2} autosize />
          <Group grow>
            <Select label="Category" data={CATEGORIES.map(c => ({ value: c, label: c.charAt(0).toUpperCase() + c.slice(1) }))} value={qCategory} onChange={v => setQCategory((v as ObservationCategory) ?? 'communication')} />
            <Select label="Phase" data={PHASES.map(p => ({ value: p, label: p.charAt(0).toUpperCase() + p.slice(1) }))} value={qPhase} onChange={v => setQPhase((v as ObservationPhase) ?? 'response')} />
          </Group>
          <Textarea label="Suggested Remedy" value={qRemedy} onChange={e => setQRemedy(e.currentTarget.value)} minRows={2} autosize />
          <Checkbox label="Submit anonymously" checked={qAnon} onChange={e => setQAnon(e.currentTarget.checked)} size="sm" />
          <Group justify="flex-end" mt="sm">
            <Button variant="subtle" onClick={closeQuick}>Cancel</Button>
            <Button onClick={handleQuickSubmit} loading={qSubmitting}>Submit Observation</Button>
          </Group>
        </Stack>
      </Modal>

      {/* Delete Confirmation */}
      <Modal opened={deleteOpened} onClose={closeDelete} title="Delete Observation" size="sm">
        <Text size="sm" mb="md">Delete "{deleteTarget?.title}"? This cannot be undone.</Text>
        <Group justify="flex-end">
          <Button variant="subtle" size="xs" onClick={closeDelete}>Cancel</Button>
          <Button color="red" size="xs" onClick={handleDelete}>Delete</Button>
        </Group>
      </Modal>
    </div>
  );
}
